import React, { useEffect, useRef, useState, useCallback } from 'react'
import Guacamole from 'guacamole-common-js'
import './styles.scss'
import { getServerInfo } from '../../utils/helpers/networkInfo.helper'
import RemoteClientManager from '../RemoteClientManage'

const RemoteClient = ({ encryptedToken, isMinimized, vmSelectedName }) => {
  const displayRef = useRef(null)
  const clientRef = useRef(null)
  const tunnelRef = useRef(null)
  const isClientConnectedRef = useRef(false)

  const [errorMessage, setErrorMessage] = useState(null)
  const [onStateMessage, setOnStateMessage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [vmDetails, setVmDetails] = useState(null)

  const MAX_RETRIES = 5 // Máximo número de reintentos
  const RETRY_DELAY = 1000 // Delay inicial en milisegundos
  const retryCountRef = useRef(0) // Contador de reintentos

  // Mover connectClient a una función memorizada con useCallback
  const connectClient = useCallback(
    (retryDelay = RETRY_DELAY) => {
      // Verificar si ya está conectado para evitar duplicados
      if (isClientConnectedRef.current) {
        setErrorMessage('Session activa, no se abrirá una nueva sesión.')
        return
      }

      let tunnel
      let client
      let display
      let guacDisplay

      let mouse
      let keyboard

      // Bloque conexion
      try {
        setIsLoading(true)

        const serverUrl = getServerInfo('ws')
        const url = `${serverUrl.replace('http', 'ws')}?token=${encryptedToken}`

        tunnel = new Guacamole.WebSocketTunnel(url)
        client = new Guacamole.Client(tunnel)

        tunnelRef.current = tunnel
        clientRef.current = client

        guacDisplay = client.getDisplay().getElement()
        display = displayRef.current

        if (!display) {
          setErrorMessage(
            'El cliente esta ocupado o se esta inciando, Re abre la ventana modal para usarlo'
          )
          return
        }

        mouse = new Guacamole.Mouse(display)
        keyboard = new Guacamole.Keyboard(display)

        tunnel.onerror = (error) => {
          setErrorMessage(`Nuevo intento de conexion: ${error.message}`)
          if (retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current += 1
            setTimeout(() => {
              connectClient(retryDelay * 2) // Retardo exponencial
            }, retryDelay)
          } else {
            setErrorMessage('Se alcanzó el número máximo de reintentos.')
          }
        }

        client.onstatechange = (state) => {
          switch (state) {
            case Guacamole.Client.State.IDLE:
              setOnStateMessage('idle.')
              break
            case Guacamole.Client.State.CONNECTING:
              setOnStateMessage('Conectando con el cliente...')
              break
            case Guacamole.Client.State.WAITING:
              setOnStateMessage('Esperando cliente...')
              break
            case Guacamole.Client.State.CONNECTED:
              isClientConnectedRef.current = true
              setOnStateMessage('Cliente conectado.')
              adjustDisplaySize()
              retryCountRef.current = 0 // Resetear el contador de reintentos al conectarse
              break
            case Guacamole.Client.State.DISCONNECTING:
              isClientConnectedRef.current = false
              setOnStateMessage('Desconectando cliente...')
              disconnectClient()
              break
            case Guacamole.Client.State.DISCONNECTED:
              isClientConnectedRef.current = false
              setOnStateMessage('Cliente desconectado.')
              break
            default:
              setOnStateMessage('El estado del cliente es desconocido: ' + state)
              break
          }
        }
      } catch (error) {
        setErrorMessage(`No se logra crear el túnel con el servidor: ${error.message}`)
      } finally {
        setIsLoading(false)
      }

      // Función para ajustar el tamaño de los elementos Guacamole
      const adjustDisplaySize = () => {
        let width = display.offsetWidth
        let height = display.offsetHeight

        client.sendSize(width, height) // Ajustar tamaño en función de la resolución
        guacDisplay.style.width = `${width}px`
        guacDisplay.style.height = `${height}px`

        // Asegurar que guacDisplay tenga el foco después del ajuste de tamaño
        if (guacDisplay && typeof guacDisplay.focus === 'function') {
          guacDisplay.focus()
        }
      }

      const drawConsole = (client) => {
        // Check if the client is connected using the isClientConnectedRef flag
        const isClientReady = () => isClientConnectedRef.current

        if (client) {
          mouse.onmousedown =
            mouse.onmouseup =
            mouse.onmousemove =
              (mouseState) => {
                if (isClientReady()) {
                  client.sendMouseState(mouseState)
                }
              }
        }

        keyboard.onkeydown = (keysym) => {
          if (isClientConnectedRef.current) {
            client.sendKeyEvent(1, keysym)
          }
        }

        keyboard.onkeyup = (keysym) => {
          if (isClientConnectedRef.current) {
            client.sendKeyEvent(0, keysym)
          }
        }

        // Asegurar que el contenedor guacDisplay tenga el foco cuando se haga clic
        display.addEventListener('click', () => {
          if (guacDisplay && typeof guacDisplay.focus === 'function') {
            guacDisplay.focus()
          }
        })

        // También capturar eventos de teclado en todo el documento
        document.addEventListener('keydown', (event) => {
          if (isClientConnectedRef.current) {
            keyboard.onkeydown(event.keyCode)
          }
        })

        document.addEventListener('keyup', (event) => {
          if (isClientConnectedRef.current) {
            keyboard.onkeyup(event.keyCode)
          }
        })
      }

      if (display) {
        drawConsole(client)

        display.appendChild(guacDisplay)
        display.onerror = (error) => {
          setErrorMessage(`Display error: ${error.message}`)
        }
        display.addEventListener('click', () => guacDisplay.focus())
        client.connect()
      }

      window.addEventListener('resize', adjustDisplaySize)
      display.addEventListener('click', () => guacDisplay.focus())
      return () => {
        window.removeEventListener('resize', adjustDisplaySize)
        document.removeEventListener('keydown', keyboard.onkeydown)
        document.removeEventListener('keyup', keyboard.onkeyup)
      }
    },
    [encryptedToken]
  ) // Dependencias añadidas

  const updateVmDetails = (details) => {
    setVmDetails(details)
  }

  const shoudlReconnect = () => {
    connectClient()
  }

  // Función para desconectar el cliente Guacamole
  const disconnectClient = () => {
    if (clientRef.current && isClientConnectedRef.current) {
      clientRef.current.disconnect()
      isClientConnectedRef.current = false
      clientRef.current = null // Resetea el cliente
      tunnelRef.current = null // Resetea el túnel
      setErrorMessage('Sesión cerrada correctamente.')
    }
  }

  // Controlar el ciclo de vida del cliente Guacamole
  useEffect(() => {
    // Conectar el cliente solo si no está minimizado y no existe un cliente
    if (!isMinimized && !isClientConnectedRef.current && encryptedToken) {
      connectClient()
    }

    // Limpiar al desmontar o cerrar la modal
    return () => {
      if (!isMinimized) {
        disconnectClient() // Desconectar solo si la modal se cierra
      }
    }
  }, [encryptedToken, isMinimized, connectClient])

  useEffect(() => {
    if (vmDetails) {
      const displayElement = displayRef.current

      if (vmDetails.vm_details.power_state === 'poweredOff') {
        if (displayElement) {
          displayElement.style.display = 'none'
        }
      }

      if (vmDetails.vm_details.power_state === 'poweredOn') {
        if (displayElement) {
          displayElement.style.display = 'block'
        }
      }
    }
  }, [vmDetails])

  return (
    <>
      {isLoading ? (
        <div className="loading-container">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '4em' }}></i>
        </div>
      ) : (
        <div className="remote-client-container">
          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {onStateMessage && <div className="state-message">{onStateMessage}</div>}
          <div ref={displayRef} tabIndex={0} className="remote-client-display" />

          <RemoteClientManager
            isConnected={isClientConnectedRef.current}
            vmName={vmSelectedName}
            onStateMessage={onStateMessage}
            updateVmDetails={updateVmDetails}
            shoudlReconnect={shoudlReconnect}
            disconnectClient={disconnectClient}
          />
        </div>
      )}
    </>
  )
}

export default RemoteClient
